/**
 * Revenue Split Panel — Admin
 *
 * Configures the per-service revenue split:
 *   Ecosystem: always 10% of gross
 *   Center:    Percentage | Flat Rate | None  (configurable per service)
 *   Enhancer:  Remainder = gross − ecosystem − center
 *
 * Completed transaction records are IMMUTABLE — changes here only apply to
 * future bookings.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil, Check, X, ChevronDown, Info, DollarSign, Percent, Minus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const pct = (v) => `${Number(v || 0).toFixed(1)}%`;

/** Mirror the backend compute_split() configurable branch. */
function previewSplit(gross, compType, compPct, compFlat) {
  if (!gross || gross <= 0) return null;
  const eco = +(gross * 0.10).toFixed(2);
  let center = 0;
  if (compType === "percent") {
    center = +(gross * (compPct || 0) / 100).toFixed(2);
  } else if (compType === "flat") {
    center = Math.min(+(compFlat || 0).toFixed(2), Math.max(0, gross - eco));
  }
  const enhancer = +(gross - eco - center).toFixed(2);
  return { eco, center, enhancer };
}

const COMP_LABELS = {
  "": "Use preset",
  none: "No center allocation",
  percent: "% of gross",
  flat: "Flat $ per booking",
};

const COMP_ICONS = {
  "": <Minus size={12} />,
  none: <Minus size={12} />,
  percent: <Percent size={12} />,
  flat: <DollarSign size={12} />,
};

function SplitBadge({ type }) {
  const colors = {
    "": "bg-[#E5E0D8] text-[#4A5D6B]",
    none: "bg-[#E5E0D8] text-[#4A5D6B]",
    percent: "bg-[#144F75]/10 text-[#144F75]",
    flat: "bg-[#5C8A72]/10 text-[#5C8A72]",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${colors[type] || colors[""]}`}>
      {COMP_ICONS[type] || COMP_ICONS[""]}
      {COMP_LABELS[type] || type}
    </span>
  );
}

function SplitBar({ eco, center, enhancer, gross }) {
  if (!gross || gross <= 0) return null;
  const epct = (eco / gross * 100).toFixed(1);
  const cpct = (center / gross * 100).toFixed(1);
  const hpct = (enhancer / gross * 100).toFixed(1);
  return (
    <div className="flex h-2 rounded-full overflow-hidden w-full mt-1.5" title={`Ecosystem ${epct}% / Center ${cpct}% / Enhancer ${hpct}%`}>
      <div style={{ width: `${epct}%` }} className="bg-[#D98E73]" />
      <div style={{ width: `${cpct}%` }} className="bg-[#9E5C7F]" />
      <div style={{ width: `${hpct}%` }} className="bg-[#5C8A72]" />
    </div>
  );
}

function EditRow({ svc, onSaved, onCancel }) {
  const [form, setForm] = useState({
    center_comp_type: svc.center_comp_type || "",
    center_comp_pct: svc.center_comp_pct || 0,
    center_comp_flat: svc.center_comp_flat || 0,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const preview = previewSplit(svc.price, form.center_comp_type, parseFloat(form.center_comp_pct) || 0, parseFloat(form.center_comp_flat) || 0);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/services/${svc.id}/split-config`, {
        center_comp_type: form.center_comp_type,
        center_comp_pct: parseFloat(form.center_comp_pct) || 0,
        center_comp_flat: parseFloat(form.center_comp_flat) || 0,
      });
      toast.success("Split config saved — applies to future bookings only.");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <tr className="bg-[#F9F7F3] border-b border-[#E5E0D8]">
      <td colSpan={5} className="p-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-[#1A242B]">
            <Pencil size={14} className="text-[#144F75]" />
            Editing split for: <span className="font-semibold">{svc.title}</span>
            <span className="text-xs text-[#8FA89B] font-normal">at {money(svc.price)}/session</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-[#4A5D6B] block mb-1">Center compensation type</label>
              <select value={form.center_comp_type} onChange={set("center_comp_type")} data-testid="split-comp-type" className="eyw-input w-full text-sm">
                <option value="">Use revenue model preset</option>
                <option value="none">None — $0 to center</option>
                <option value="percent">Percentage of gross</option>
                <option value="flat">Flat $ per completed booking</option>
              </select>
            </div>
            {form.center_comp_type === "percent" && (
              <div>
                <label className="text-xs text-[#4A5D6B] block mb-1">Center % of gross</label>
                <div className="relative">
                  <input type="number" min={0} max={89} step={0.5} value={form.center_comp_pct}
                    onChange={set("center_comp_pct")} data-testid="split-comp-pct" className="eyw-input w-full text-sm pr-7" />
                  <Percent size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8FA89B]" />
                </div>
              </div>
            )}
            {form.center_comp_type === "flat" && (
              <div>
                <label className="text-xs text-[#4A5D6B] block mb-1">Flat $ per booking</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8FA89B] text-xs">$</span>
                  <input type="number" min={0} step={0.01} value={form.center_comp_flat}
                    onChange={set("center_comp_flat")} data-testid="split-comp-flat" className="eyw-input w-full text-sm pl-6" />
                </div>
              </div>
            )}
          </div>

          {/* Live preview */}
          {preview && form.center_comp_type && form.center_comp_type !== "" && (
            <div className="rounded-xl bg-[#F1EEE8] border border-[#E5E0D8] p-3 text-sm" data-testid="split-preview">
              <p className="text-xs font-semibold text-[#4A5D6B] mb-2 flex items-center gap-1.5"><Info size={11} /> Breakdown at {money(svc.price)}</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-[#D98E73]/10 rounded-lg p-2">
                  <p className="text-[10px] text-[#B5794E] font-semibold uppercase tracking-wide">Ecosystem (10%)</p>
                  <p className="font-bold text-[#B5794E] text-base" data-testid="split-eco">{money(preview.eco)}</p>
                </div>
                <div className="bg-[#9E5C7F]/10 rounded-lg p-2">
                  <p className="text-[10px] text-[#7D4968] font-semibold uppercase tracking-wide">
                    Center {form.center_comp_type === "percent" ? `(${form.center_comp_pct || 0}%)` : "(flat)"}
                  </p>
                  <p className="font-bold text-[#7D4968] text-base" data-testid="split-center">{money(preview.center)}</p>
                </div>
                <div className="bg-[#5C8A72]/10 rounded-lg p-2">
                  <p className="text-[10px] text-[#3E6B54] font-semibold uppercase tracking-wide">Enhancer (remainder)</p>
                  <p className="font-bold text-[#3E6B54] text-base" data-testid="split-enhancer">{money(preview.enhancer)}</p>
                </div>
              </div>
              <SplitBar eco={preview.eco} center={preview.center} enhancer={preview.enhancer} gross={svc.price} />
              <p className="text-[10px] text-[#8FA89B] mt-1.5">Orange = Ecosystem · Purple = Center · Green = Enhancer</p>
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button onClick={save} disabled={busy} data-testid="split-save" className="eyw-btn !py-1.5 text-sm disabled:opacity-60">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
            </button>
            <button onClick={onCancel} className="eyw-btn-ghost !py-1.5 text-sm"><X size={14} /> Cancel</button>
            <p className="text-[11px] text-[#8FA89B] ml-1">Completed transactions are <strong>not</strong> affected.</p>
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function RevenueSplitPanel() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // service id being edited
  const [q, setQ] = useState("");
  const { data: svcs = [], isLoading } = useQuery({
    queryKey: ["admin-services-split"],
    queryFn: () => api.get("/admin/services").then((r) => r.data),
  });

  const filtered = svcs.filter((s) =>
    !q || s.title?.toLowerCase().includes(q.toLowerCase()) ||
    s.enhancer_name?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="revenue-split-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-serif text-xl text-[#1A242B]">Revenue Split Configuration</h3>
          <p className="text-xs text-[#8FA89B] mt-0.5 max-w-xl">
            Set the center allocation for each service. The ecosystem always receives 10% of gross. The enhancer receives the remainder.
            Completed transaction records are <strong>immutable</strong> — changes only apply to future bookings.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-[#4A5D6B] bg-[#F1EEE8] px-3 py-2 rounded-xl">
          <div className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-[#D98E73] inline-block" /> Ecosystem 10%</div>
          <div className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-[#9E5C7F] inline-block" /> Center (configurable)</div>
          <div className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-[#5C8A72] inline-block" /> Enhancer (remainder)</div>
        </div>
      </div>

      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by service title or enhancer…"
        data-testid="split-search" className="eyw-input w-full text-sm" />

      {isLoading ? (
        <div className="text-[#8FA89B] flex items-center gap-2 text-sm py-6"><Loader2 size={15} className="animate-spin" /> Loading services…</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#E5E0D8]">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-[#8FA89B] bg-[#F9F7F3] border-b border-[#E5E0D8]">
                <th className="py-2.5 px-3 font-semibold">Service</th>
                <th className="py-2.5 px-3 font-semibold">Price</th>
                <th className="py-2.5 px-3 font-semibold">Center Split</th>
                <th className="py-2.5 px-3 font-semibold hidden sm:table-cell">Breakdown (at price)</th>
                <th className="py-2.5 px-3 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((svc) => {
                const p = previewSplit(svc.price, svc.center_comp_type, svc.center_comp_pct, svc.center_comp_flat);
                const isEditing = editing === svc.id;
                return [
                  <tr key={svc.id} className={`border-b border-[#F1EEE8] hover:bg-[#FDFCF9] ${isEditing ? "bg-[#F9F7F3]" : ""}`} data-testid={`split-row-${svc.id}`}>
                    <td className="py-2.5 px-3">
                      <p className="font-medium text-[#1A242B] leading-tight">{svc.title}</p>
                      {svc.enhancer_name && <p className="text-[11px] text-[#8FA89B]">{svc.enhancer_name}</p>}
                    </td>
                    <td className="py-2.5 px-3 font-medium text-[#1A242B]">{money(svc.price)}</td>
                    <td className="py-2.5 px-3">
                      <SplitBadge type={svc.center_comp_type || ""} />
                      {svc.center_comp_type === "percent" && <span className="ml-1 text-xs text-[#144F75]">{pct(svc.center_comp_pct)}</span>}
                      {svc.center_comp_type === "flat" && <span className="ml-1 text-xs text-[#5C8A72]">{money(svc.center_comp_flat)}</span>}
                    </td>
                    <td className="py-2.5 px-3 hidden sm:table-cell">
                      {p && svc.center_comp_type ? (
                        <div className="text-xs space-y-0.5">
                          <div className="flex items-center gap-2">
                            <span className="text-[#B5794E]">Eco {money(p.eco)}</span>
                            <span className="text-[#7D4968]">Center {money(p.center)}</span>
                            <span className="text-[#3E6B54] font-semibold">Enh {money(p.enhancer)}</span>
                          </div>
                          <SplitBar eco={p.eco} center={p.center} enhancer={p.enhancer} gross={svc.price} />
                        </div>
                      ) : (
                        <span className="text-[11px] text-[#8FA89B] italic">Using revenue model preset</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <button onClick={() => setEditing(isEditing ? null : svc.id)}
                        data-testid={`split-edit-${svc.id}`}
                        className="text-xs text-[#144F75] hover:underline inline-flex items-center gap-1">
                        {isEditing ? <><X size={12} /> Cancel</> : <><Pencil size={12} /> Configure</>}
                      </button>
                    </td>
                  </tr>,
                  isEditing && (
                    <EditRow key={`edit-${svc.id}`} svc={svc}
                      onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["admin-services-split"] }); }}
                      onCancel={() => setEditing(null)} />
                  ),
                ].filter(Boolean);
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-[#8FA89B] text-sm">No services found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div className="rounded-xl bg-[#F1EEE8] border border-[#E5E0D8] p-4 text-sm">
        <p className="font-semibold text-[#1A242B] mb-2">Formula</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-[#4A5D6B]">
          <div className="bg-white rounded-lg p-3 border border-[#E5E0D8]">
            <p className="font-semibold text-[#B5794E] mb-0.5">Ecosystem Allocation</p>
            <p className="font-mono">10% × gross</p>
            <p className="text-[#8FA89B] mt-0.5">Always 10%. Applies to all digitally processed payments including tips. Cash tips excluded.</p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-[#E5E0D8]">
            <p className="font-semibold text-[#7D4968] mb-0.5">Center Allocation</p>
            <p className="font-mono">configured %/$ or $0</p>
            <p className="text-[#8FA89B] mt-0.5">Percentage of gross, flat $ per booking, or $0 for virtual/non-center services.</p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-[#E5E0D8]">
            <p className="font-semibold text-[#3E6B54] mb-0.5">Enhancer Remainder</p>
            <p className="font-mono">gross − ecosystem − center</p>
            <p className="text-[#8FA89B] mt-0.5">The enhancer receives everything left after ecosystem and center shares are taken.</p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-[#E5E0D8] text-xs text-[#4A5D6B] space-y-1">
          <p><strong>$100 service, 20% center:</strong> Ecosystem $10 / Center $20 / Enhancer $70</p>
          <p><strong>$100 service, $25 flat center:</strong> Ecosystem $10 / Center $25 / Enhancer $65</p>
          <p><strong>$100 service, no center:</strong> Ecosystem $10 / Enhancer $90</p>
        </div>
      </div>
    </div>
  );
}
